import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { X, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/* ==========================================================================
   Toast notification system
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  /** Auto-dismiss after ms. Default 4000. Set 0 to persist. */
  duration: number;
}

type AddToastOptions = Omit<Toast, 'id'> & { id?: string };

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: AddToastOptions) => void;
  removeToast: (id: string) => void;
}

/* --------------------------------------------------------------------------
   Context
   -------------------------------------------------------------------------- */

const ToastContext = createContext<ToastContextValue | null>(null);

let _counter = 0;

/* --------------------------------------------------------------------------
   Provider
   -------------------------------------------------------------------------- */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((opts: AddToastOptions) => {
    const id = opts.id ?? `toast-${++_counter}`;
    const toast: Toast = { ...opts, id, duration: opts.duration ?? 4000 };
    setToasts((prev) => [...prev, toast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

/* --------------------------------------------------------------------------
   Hook
   -------------------------------------------------------------------------- */

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');

  const { addToast, removeToast } = ctx;

  return {
    success: (message: string, duration = 4000) =>
      addToast({ message, variant: 'success', duration }),
    error: (message: string, duration = 5000) =>
      addToast({ message, variant: 'error', duration }),
    warning: (message: string, duration = 4500) =>
      addToast({ message, variant: 'warning', duration }),
    info: (message: string, duration = 4000) =>
      addToast({ message, variant: 'info', duration }),
    dismiss: removeToast,
  };
}

/* --------------------------------------------------------------------------
   Toast container (portal-free, renders at top-center)
   -------------------------------------------------------------------------- */

function ToastContainer() {
  const ctx = useContext(ToastContext);
  if (!ctx || ctx.toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-label="Notifiche"
      className={cn(
        'fixed top-4 left-1/2 -translate-x-1/2 z-[var(--z-toast)]',
        'flex flex-col items-center gap-2',
        'w-full max-w-md px-4',
        'pointer-events-none',
      )}
    >
      {ctx.toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={ctx.removeToast} />
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Individual toast
   -------------------------------------------------------------------------- */

const VARIANT_STYLES: Record<
  ToastVariant,
  { icon: typeof CheckCircle; containerCn: string }
> = {
  success: {
    icon: CheckCircle,
    containerCn: 'border-status-positive/30 bg-surface-card',
  },
  error: {
    icon: XCircle,
    containerCn: 'border-status-negative/30 bg-surface-card',
  },
  warning: {
    icon: AlertTriangle,
    containerCn: 'border-[#C49A4A]/30 bg-surface-card',
  },
  info: {
    icon: Info,
    containerCn: 'border-status-info/30 bg-surface-card',
  },
};

const VARIANT_ICON_COLORS: Record<ToastVariant, string> = {
  success: 'text-status-positive',
  error: 'text-status-negative',
  warning: 'text-[#B08A3E]',
  info: 'text-status-info',
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (toast.duration <= 0) return;
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, toast.duration);
    return () => clearTimeout(timerRef.current);
  }, [toast.duration]);

  // After exit animation, remove from state
  const handleAnimationEnd = () => {
    if (exiting) onDismiss(toast.id);
  };

  const { icon: Icon, containerCn } = VARIANT_STYLES[toast.variant];

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto',
        'w-full rounded-[var(--radius-lg)] border px-4 py-3',
        'shadow-lg',
        'flex items-start gap-3',
        containerCn,
        exiting ? 'toast-exit' : 'toast-enter',
      )}
      onAnimationEnd={handleAnimationEnd}
    >
      <Icon
        className={cn(
          'h-5 w-5 shrink-0 mt-0.5',
          VARIANT_ICON_COLORS[toast.variant],
        )}
        aria-hidden
      />
      <p className="flex-1 text-sm font-medium text-text-primary leading-snug">
        {toast.message}
      </p>
      <button
        onClick={() => setExiting(true)}
        className="shrink-0 text-text-muted hover:text-text-primary transition-colors cursor-pointer"
        aria-label="Chiudi notifica"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
